import { Discount } from "./Discount";
import { StoreMinimalModel, StorePickupBox, StorePickupBoxId } from "./Store";
import { PersonMinimalModel } from "./Person";
import { ProductMinimalModel } from "./Product";
import { Tenant, TenantTypeName } from "./Tenant";
import { LoginMethod } from "./Market";

export interface Anonymous {
  readonly type: "ANONYMOUS";
}

export interface TenentColorTheme {
  readonly appBarColor: string;
}

export interface TenantConfiguration {
  readonly name: string;
  readonly colorTheme: TenentColorTheme;
  readonly features: string[];
}

export interface Authenticated {
  readonly type: "AUTHENTICATED";
  readonly id: string;
  readonly name: string;
  readonly email: string;
  readonly phoneNumber: string;
  readonly roles: string[];
  readonly distributorId: string;
  readonly tenant: Tenant;
  readonly tenantIds: string[];
}

export enum UserStatus {
  None = "None",
  Verified = "Verified",
  Suspicious = "Suspicious",
  Locked = "Locked"
}

export enum AdministratorEvent {
  EmployeeStatus = "EmployeeStatus",
  UserStatus = "UserStatus",
  Lock = "Lock",
  Unlock = "Unlock",
  AddPurchase = "AddPurchase",
  AddComment = "AddComment",
  VerifyPurchase = "VerifyPurchase"
}

export type AdminUser = Anonymous | Authenticated;

export const anonymous = (): AdminUser => ({
  type: "ANONYMOUS"
});

export interface LifvsUser {
  readonly id: UserId;
  readonly birthDate: string;
  readonly loginMethodIdentifier: string;
  readonly loginMethod: LoginMethod;
  readonly email: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly deviceLanguage: string;
  readonly registrationDate: Date;
  readonly hasRegisteredCreditCard: boolean;
  readonly averagePurchaseAmount: number;
  readonly discounts: Array<UserDiscount>;
  readonly receipts: Array<Receipt>;
  readonly notifications: Array<UserNotification>;
  readonly topPurchasedProducts: Array<TopPurchasedProduct>;
  readonly authenticationType: string;
  readonly address: string;
  readonly zipCode: string;
  readonly city: string;
  readonly phoneNumber: string;
  readonly lockoutReason?: string;
  readonly allowPushCommunication: boolean;
  readonly status: UserStatus;
  readonly employee: boolean;
  readonly lockedBy?: string;
  readonly openDoorEventCount: number;
  readonly paymentEventCount: number;
  readonly lastStatusChange: Date;
}

export interface LifvsUserMinimalModel {
  readonly id: UserId;
  readonly email: string;
  readonly firstName: string;
  readonly lastName: string;
}

export interface LifvsUserEventDynamicData {
  readonly chargeId?: string;
  readonly storeId?: string;
  readonly receiptItems?: [];
  readonly paymentMethod?: string;
  readonly eventTenantItem?: UserEventTenantItem;
  readonly groupedActions?: LifvsUserEvent[];
}

export interface UserEventTenantItem {
  readonly tenantItemId: string;
  readonly tenantItemName: string;
}

export interface LifvsUserEvent {
  readonly sessionId: string;
  readonly userId: string;
  readonly store: StoreMinimalModel;
  readonly event: string;
  readonly timestamp: Date;
  readonly successful: boolean;
  readonly dynamicData: LifvsUserEventDynamicData;
}

export interface LifvsAdministratorEventDynamicData {
  readonly lockOutReason?: number;
  readonly comment?: string;
  readonly paymentFailedCount?: number;
  readonly status?: string;
  readonly employee?: boolean;
  readonly verified?: boolean;
  readonly declined?: boolean;
}

export interface LifvsAdministratorEvent {
  readonly id: string;
  readonly timeStamp: Date;
  readonly userId: string;
  readonly userName: string;
  readonly event: string;
  readonly successful: boolean;
  readonly targetId: string;
  readonly data: LifvsAdministratorEventDynamicData;
}

export type UserId = string;

export interface UserDiscount extends Discount {
  readonly used: boolean;
  readonly active: boolean;
  readonly added: Date;
}

export interface Receipt {
  readonly userId: string;
  readonly id: number;

  readonly currency: string;
  readonly netAmount: number;
  readonly grossAmount: number;
  readonly createdDate: Date;
  readonly discountItem?: ReceiptDiscountItem;
  readonly paymentMethodType?: string;
  items: Array<ReceiptItem>;
  readonly store: StoreMinimalModel;
}

export interface ReceiptItem {
  readonly id: string;
  readonly receiptId: number;
  readonly brandName?: string;
  readonly productName: string;
  readonly size: string;
  readonly depositFee?: number;
  readonly price: number;
  readonly itemAmount: number;
  readonly vat: number;
  readonly vatPercentage: number;
  readonly quantity: number;
  readonly refundQuantity?: number;
  readonly discountedPrice?: number;
  readonly campaignName?: string;
}

export interface UserNotification {
  readonly title: string;
  readonly body: string;
  readonly created: Date;
}

export interface TopPurchasedProduct {
  readonly productName: string;
  readonly count: string;
}

export interface ReceiptDiscountItem {
  amount: number;
  amountType: DiscountAmountType;
}

export enum DiscountAmountType {
  Percentage = "Percentage",
  Amount = "Amount"
}

export type UserOrderId = string;
export type UserOrderItemId = string;

export enum UserOrderType {
  ClickNCollect = "ClickNCollect",
  HomeDelivery = "HomeDelivery"
}

export enum UserOrderState {
  Ordered = "Ordered",
  Processing = "Processing",
  AwaitingPickup = "AwaitingPickup",
  Delivered = "Delivered",
  Cancelled = "Cancelled"
}

export interface UserOrderDiscountItem {}

export interface UserOrderItem {
  readonly id: UserOrderItemId;
  readonly product: ProductMinimalModel;

  readonly campaignId?: string;
  readonly price: number;

  readonly orderedQuantity: number;
  deliveredQuantity?: number;
}

export interface UserOrder {
  readonly id: UserOrderId;
  readonly type: UserOrderType;
  readonly state: UserOrderState;

  readonly user: LifvsUserMinimalModel;
  readonly store: StoreMinimalModel;

  pickupBoxes: ReadonlyArray<StorePickupBox>;
  readonly lastUpdatedBy?: PersonMinimalModel;

  readonly created: Date;
  readonly updated: Date;
  deadline?: Date;

  readonly items: ReadonlyArray<UserOrderItem>;
  readonly discount: UserOrderDiscountItem;

  comment?: string;
}

export interface UserOrderItemWriteModel {
  readonly id: UserOrderItemId;
  readonly deliveredQuantity: number;
}

export interface UserOrderWriteModel {
  readonly id: UserOrderId;
  readonly userId: UserId;
  readonly storePickupBoxes: ReadonlyArray<StorePickupBoxId>;
  readonly state: UserOrderState;
  readonly deadline?: Date;
  readonly comment?: string;
  readonly items: ReadonlyArray<UserOrderItemWriteModel>;
}

export interface UserLocation {
  storeId: string;
  storeName: string;
  updated: Date;
  showReminder: boolean;
}

export const toUserOrderWriteModel = (
  userOrder: UserOrder
): UserOrderWriteModel => ({
  id: userOrder.id,
  userId: userOrder.user.id,
  deadline: userOrder.deadline,
  state: userOrder.state,
  comment: userOrder.comment,
  items: userOrder.items.map(
    i =>
      ({
        id: i.id,
        deliveredQuantity: i.deliveredQuantity
      } as UserOrderItemWriteModel)
  ),
  storePickupBoxes:
    userOrder.pickupBoxes && userOrder.pickupBoxes.map(pb => pb.id)
});

export const hasRole = (
  user: AdminUser,
  allowedRoles: ReadonlyArray<string>
): boolean =>
  user.type !== "ANONYMOUS" &&
  user.roles.some(role => new Set(allowedRoles).has(role));

export const isProductCampaignReadOnly = (user: AdminUser): boolean =>
  hasRole(user, ["ITAdmin", "ITSupport"]) &&
  !hasRole(user, ["Root", "Support"]);

export const isWhiteLabelSegmentCampaignsOnly = (user: AdminUser): boolean =>
  user.type !== "ANONYMOUS" &&
  user.tenant.tenantType.name === TenantTypeName.WhiteLabel &&
  !hasRole(user, ["Root", "Support", "ITAdmin", "ITSupport"]);

export const hasDeniedRole = (
  user: AdminUser,
  deniedRoles: ReadonlyArray<string>
): boolean => {
  if (user.type === "ANONYMOUS") {
    return false;
  }

  const denied = new Set(deniedRoles);
  let allowedRoles = user.roles.length;

  user.roles.forEach(role => {
    if (denied.has(role)) {
      allowedRoles--;
    }
  });

  return allowedRoles <= 0;
};

export interface RegisterPurchase {
  customerId: string;
  storeId: string;

  chargeId: string;
  userDiscountId: string;

  timeOfPurchase: string;

  items: Array<{
    productId: string;
    quantity: number;
    price: number;
  }>;
}

export interface UserStatusCount {
  total: number;
  last24: number;
  last24before: number;
}
